/**
 * Read layout as it WILL be once the transitions running under `root` have
 * finished.
 *
 * A move that has to land exactly (the composer rising to meet a phone
 * keyboard) is aimed once, and the first focus of a page is still collapsing
 * the hero copy above the composer when that happens. Aiming at the rect of
 * the moment lands short by whatever the collapse has left to travel; waiting
 * for it to finish turns one motion into two.
 *
 * Every finite animation under `root` is held at its last moment for the
 * duration of `read` and put back before the browser paints, so nothing is
 * seen and none of them is restarted. `root`'s own animations are left alone:
 * a caller moving `root` itself knows where it is sending it.
 *
 * Animations that already report "finished" are held too. Between two frames
 * WebKit answers `playState` from the live clock but keeps laying out with the
 * values of the last frame it drew, and a phone bringing up its keyboard can
 * go most of a second without drawing one: the transition says it is over
 * while every rect still shows it part-way. Seeking it is what makes the
 * engine lay it out again.
 */
export function readSettledLayout<T>(root: Element, read: () => T): T {
  const held: Array<{ animation: Animation; time: CSSNumberish | null }> = [];
  for (const animation of root.getAnimations?.({ subtree: true }) ?? []) {
    const effect = animation.effect;
    if (animation.playState === 'idle' || !(effect instanceof KeyframeEffect) || effect.target === root) continue;
    const end = effect.getComputedTiming().endTime;
    if (typeof end !== 'number' || !Number.isFinite(end)) continue;
    held.push({ animation, time: animation.currentTime });
    // A hair before the end: the values are the final ones, and an animation
    // still running never becomes "finished", so no end event is queued.
    animation.currentTime = Math.max(0, end - 1);
  }
  try {
    return read();
  } finally {
    for (const { animation, time } of held) animation.currentTime = time;
  }
}
